import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PayloadContent } from '../../../../viewmodels/apple/profiles/payload-content.model';

@Component({
  selector: 'ml-mdm-configuration-payload',
  templateUrl: './configuration-payload.component.html',
  styleUrls: ['./configuration-payload.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class ConfigurationPayloadComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly payloadContent = input.required<PayloadContent>();
  readonly mutate = input(false);
  readonly allowToggle = input(true);

  readonly payloadContentChange = output<PayloadContent>();

  public editing = false;
  public payloadContentForm: UntypedFormGroup;

  constructor() {
    this.createForm();

    effect(() => {
      const payloadContent = this.payloadContent();
      this.rebuildForm(payloadContent);
    });
  }

  doneEditing(): boolean {
    if (!this.editing) {
      return true;
    }

    if (this.payloadContentForm.status !== 'VALID') {
      return false;
    }

    const result = this.populate();
    this.payloadContentChange.emit(result);
    return true;
  }

  toggleEditing() {
    // Toggle if we are not editing, or if the edited form validates
    if (!this.editing || this.doneEditing()) {
      this.editing = !this.editing;
    }
  }

  private createForm() {
    this.payloadContentForm = this.formBuilder.group({
      payloadVersion: 0,
      payloadIdentifier: '',
      payloadUUID: '',
      payloadDisplayName: '',
      payloadDescription: '',
      payloadOrganization: '',
    });
  }

  private rebuildForm(payloadContent: PayloadContent) {
    this.payloadContentForm.reset({
      payloadVersion: payloadContent.payloadVersion,
      payloadIdentifier: payloadContent.payloadIdentifier,
      payloadUUID: payloadContent.payloadUUID,
      payloadDisplayName: payloadContent.payloadDisplayName,
      payloadDescription: payloadContent.payloadDescription,
      payloadOrganization: payloadContent.payloadOrganization,
    });
  }

  private populate(): PayloadContent {
    let payloadContent = this.payloadContent();
    if (!this.mutate()) {
      payloadContent = new PayloadContent();
      payloadContent.payloadType = this.payloadContent().payloadType;
    }

    payloadContent.payloadVersion = this.payloadContentForm.get('payloadVersion')?.value;
    payloadContent.payloadIdentifier = this.payloadContentForm.get('payloadIdentifier')?.value;
    payloadContent.payloadUUID = this.payloadContentForm.get('payloadUUID')?.value;
    payloadContent.payloadDisplayName = this.payloadContentForm.get('payloadDisplayName')?.value;
    payloadContent.payloadDescription = this.payloadContentForm.get('payloadDescription')?.value;
    payloadContent.payloadOrganization = this.payloadContentForm.get('payloadOrganization')?.value;

    return payloadContent;
  }
}
